/** @jsxImportSource @emotion/react */
import type { HTMLAttributes } from 'react'
import styled from '@emotion/styled/macro'
import type { StyledComponent } from '@emotion/styled'
import { t } from '@sportninja/common/i18n'
import { TIMEOUT_TEXT } from '@sportninja/common/features/game/scoring/constants/timeout'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import {
  heading2,
  paragraphMedium,
  weightMedium,
} from '../../../components/css'
import FormSheet from '../../../components/Form/FormSheet'
import { scoringTimeoutCountForm } from '../../../components/Form/form-configs'
import { SidewayText } from '../GamePageHeaderMatchupCss'
import { Pencil } from './TimelineEvent'

/**
 * The rows' Home and Visiting markers. `SidewayText` is JavaScript: its `right`
 * prop, which turns the visiting side's marker to read bottom to top, is typed
 * here.
 */
const SideMarker: StyledComponent<
  { right?: boolean },
  HTMLAttributes<HTMLDivElement>
> = SidewayText

/** What the sheet's form hands over: the count as typed in its number field. */
export interface TimeoutCountValues {
  timeouts: string
}

/** Sets the count. Resolves true once it is saved, which closes the sheet. */
export type SubmitTimeoutCount = (
  values: TimeoutCountValues
) => Promise<boolean>

interface CountValueProps {
  count: number
  isVisiting: boolean
}

/** The number, on the side of the tile nearest the label. */
const CountValue = ({ count, isVisiting }: CountValueProps) =>
  isVisiting ? (
    <VisitingValue>{count}</VisitingValue>
  ) : (
    <HomeValue>{count}</HomeValue>
  )

interface EditableCountProps extends CountValueProps {
  onClick: () => void
}

/**
 * A count while the timeline is edited: the team's side, a pencil and the
 * number, mirrored for the visiting team, as on the editable rows around it.
 * A click opens the sheet that sets it.
 */
const EditableCount = ({ count, isVisiting, onClick }: EditableCountProps) =>
  isVisiting ? (
    <EditableTile onClick={onClick}>
      <CountValue count={count} isVisiting />
      <EditPencil />
      <SideMarker right>Visiting</SideMarker>
    </EditableTile>
  ) : (
    <EditableTile onClick={onClick}>
      <SideMarker>Home</SideMarker>
      <EditPencil />
      <CountValue count={count} isVisiting={false} />
    </EditableTile>
  )

interface TeamCountProps extends CountValueProps {
  isEditMode: boolean
  periodName: string
  onSubmit: SubmitTimeoutCount
}

/**
 * One team's count. While the timeline is edited it opens the sheet that sets
 * it, as a count of points or shots does.
 */
const TeamCount = ({
  count,
  isVisiting,
  isEditMode,
  periodName,
  onSubmit,
}: TeamCountProps) => {
  if (!isEditMode) {
    return (
      <Count>
        <CountValue count={count} isVisiting={isVisiting} />
      </Count>
    )
  }

  const title = capitalizeWords(
    t(
      isVisiting ? TIMEOUT_TEXT.editVisitingCount : TIMEOUT_TEXT.editHomeCount,
      {}
    )
  )

  return (
    <FormSheet
      form={scoringTimeoutCountForm({ timeouts: count })}
      label={title}
      title={`${title} - ${periodName}`}
      onSubmit={onSubmit}
      renderButton={(open) => (
        <EditableCount count={count} isVisiting={isVisiting} onClick={open} />
      )}
    />
  )
}

interface TimelineTimeoutsProps {
  homeTimeouts: number
  visitingTimeouts: number
  isEditMode: boolean
  /** Names the period on the sheets that set its counts. */
  periodName: string
  onSubmitHome: SubmitTimeoutCount
  onSubmitVisiting: SubmitTimeoutCount
  className?: string
}

/**
 * A period's timeouts on a basic-stats game's timeline: each team's count
 * either side of the label, as the Points, Penalties and Shots rows show
 * theirs, without a time. While the timeline is edited each count can be set,
 * as Points and Shots can.
 */
const TimelineTimeouts = ({
  homeTimeouts,
  visitingTimeouts,
  isEditMode,
  periodName,
  onSubmitHome,
  onSubmitVisiting,
  className,
}: TimelineTimeoutsProps) => (
  <Row className={className}>
    <TeamCount
      count={homeTimeouts}
      isVisiting={false}
      isEditMode={isEditMode}
      periodName={periodName}
      onSubmit={onSubmitHome}
    />
    <Label>{t(TIMEOUT_TEXT.rowTitle, {})}</Label>
    <TeamCount
      count={visitingTimeouts}
      isVisiting
      isEditMode={isEditMode}
      periodName={periodName}
      onSubmit={onSubmitVisiting}
    />
  </Row>
)

export default TimelineTimeouts

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 28px;
`

const Count = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 141px;
  min-height: 76px;
  padding: 12px;
  ${heading2}
  ${weightMedium}
  letter-spacing: 2.07px;
`

/** The same tile, hover and pencil highlight as the editable rows around it. */
const EditableTile = styled(Count)`
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.05);
  transition: background-color 0.2s ease-in;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);

    .pencil-icon {
      background-color: rgba(255, 255, 255, 0.08);
    }
  }
`

const EditPencil = styled(Pencil)`
  display: flex;
`

const HomeValue = styled.span`
  margin-left: 12px;
  opacity: 0.5;
`

const VisitingValue = styled.span`
  margin-right: 12px;
  opacity: 0.5;
`

const Label = styled.div`
  min-width: 108px;
  margin-top: 3px;
  ${paragraphMedium}
  letter-spacing: 0.92px;
  text-align: center;
  text-transform: uppercase;
`
